import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Card, CardContent, Grid, Button, Divider,
    Chip, Alert, CircularProgress, Paper, Skeleton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import DescriptionIcon from '@mui/icons-material/Description';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LockIcon from '@mui/icons-material/Lock';
import { getOrderDetailRequest, getOrderReportRequest, resetGetOrderDetail } from '../../redux/modules/externalReferrer/slice';

const ExternalOrderDetail = () => {
    const { orderId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { orderDetailRes, orderReportRes, loading } = useSelector(state => state.externalReferrer);
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        if (orderId) {
            dispatch(getOrderDetailRequest(orderId));
            dispatch(getOrderReportRequest(orderId));
        }
        return () => {
            dispatch(resetGetOrderDetail());
        };
    }, [orderId, dispatch]);

    const order = orderDetailRes?.data;
    const report = orderReportRes?.data;

    const handleDownloadPdf = async () => {
        setDownloading(true);
        try {
            const authToken = localStorage.getItem("item");
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_API}api/external/order/${orderId}/download-pdf`,
                {
                    method: 'POST',
                    headers: {
                        'Authorization': authToken,
                        'Content-Type': 'application/json'
                    }
                }
            );

            if (!response.ok) throw new Error('Download failed');

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `Report_${order?.patient?.name || 'Patient'}.pdf`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            a.remove();
        } catch (error) {
            console.error('Download error:', error);
        } finally {
            setDownloading(false);
        }
    };

    const handleViewImages = () => {
        if (order?.hasImages && order?.accessLevel === 'report_and_images') {
            window.open(`/external/order/${orderId}/images`, '_blank');
        }
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString();
    };

    // Loading skeleton
    if (loading && !order) {
        return (
            <Box>
                <Skeleton variant="text" width={200} height={40} sx={{ mb: 3 }} />
                <Skeleton variant="text" width={300} height={50} sx={{ mb: 3 }} />
                <Grid container spacing={3}>
                    {[1, 2, 3].map((i) => (
                        <Grid item xs={12} md={4} key={i}>
                            <Skeleton variant="rounded" height={200} />
                        </Grid>
                    ))}
                </Grid>
            </Box>
        );
    }

    if (!order) {
        return (
            <Box>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/external')}
                    sx={{ mb: 3 }}
                >
                    Back to Orders
                </Button>
                <Alert severity="error" sx={{ borderRadius: 2 }}>
                    Order not found or you don't have access to view it.
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            {/* Back Button */}
            <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/external')}
                sx={{ mb: 3, textTransform: 'none' }}
            >
                Back to Orders
            </Button>

            {/* Header */}
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: 2,
                mb: 4
            }}>
                <Box>
                    <Typography variant="h4" fontWeight={700} color="primary.dark">
                        Order: {order.orderId}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {order.patient?.name || 'Unknown Patient'}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {order.accessLevel === 'report_and_images' && order.hasImages && (
                        <Button
                            variant="outlined"
                            startIcon={<ImageIcon />}
                            onClick={handleViewImages}
                            sx={{ textTransform: 'none' }}
                        >
                            Open Images Tab
                        </Button>
                    )}
                    {order.report && (
                        <Button
                            variant="contained"
                            startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                            onClick={handleDownloadPdf}
                            disabled={downloading}
                            sx={{ textTransform: 'none' }}
                        >
                            Download PDF
                        </Button>
                    )}
                </Box>
            </Box>

            <Grid container spacing={3}>
                {/* Patient Information */}
                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <PersonIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight={600}>Patient Information</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Name</Typography>
                                    <Typography fontWeight={500}>{order.patient?.name || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Date of Birth</Typography>
                                    <Typography fontWeight={500}>{formatDate(order.patient?.dob)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Gender</Typography>
                                    <Typography fontWeight={500}>{order.patient?.gender || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Age</Typography>
                                    <Typography fontWeight={500}>{order.patient?.age || '-'}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Order Information */}
                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CalendarTodayIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight={600}>Order Details</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Order ID</Typography>
                                    <Typography fontWeight={500}>{order.orderId}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Date</Typography>
                                    <Typography fontWeight={500}>{formatDate(order.appointmentDate)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Exams</Typography>
                                    <Typography fontWeight={500}>{order.examNames?.join(', ') || '-'}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Reason</Typography>
                                    <Typography fontWeight={500}>{order.examReason || '-'}</Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Access Information */}
                <Grid item xs={12} md={4}>
                    <Card elevation={2} sx={{ height: '100%', borderRadius: 2 }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <LockIcon color="primary" sx={{ mr: 1 }} />
                                <Typography variant="h6" fontWeight={600}>Access Details</Typography>
                            </Box>
                            <Divider sx={{ mb: 2 }} />
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Access Level</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            icon={order.accessLevel === 'report_and_images' ? <ImageIcon /> : <DescriptionIcon />}
                                            label={order.accessLevel === 'report_and_images' ? 'Report + Images' : 'Report Only'}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: order.accessLevel === 'report_and_images' ? 'primary.main' : 'grey.200',
                                                color: order.accessLevel === 'report_and_images' ? 'primary.contrastText' : 'text.primary',
                                                '& .MuiChip-icon': {
                                                    color: order.accessLevel === 'report_and_images' ? 'primary.contrastText' : 'text.secondary',
                                                }
                                            }}
                                        />
                                    </Box>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Expires</Typography>
                                    <Typography fontWeight={500}>{formatDate(order.accessExpiresAt)}</Typography>
                                </Box>
                                <Box>
                                    <Typography variant="caption" color="text.secondary">Images Status</Typography>
                                    <Box sx={{ mt: 0.5 }}>
                                        <Chip
                                            label={order.hasImages ? 'Available' : 'Not Available'}
                                            size="small"
                                            sx={{
                                                fontWeight: 600,
                                                bgcolor: order.hasImages ? 'success.light' : 'grey.200',
                                                color: order.hasImages ? 'success.dark' : 'text.secondary',
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Report */}
                {report && (
                    <Grid item xs={12}>
                        <Paper elevation={2} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
                            <Typography variant="h5" fontWeight={700} gutterBottom color="primary.dark">
                                Diagnostic Report
                            </Typography>
                            <Divider sx={{ mb: 3 }} />

                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Reporting Physician</Typography>
                                    <Typography fontWeight={500}>{report.radiologist || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Report Date</Typography>
                                    <Typography fontWeight={500}>{formatDate(report.reportDate)}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Referring Doctor</Typography>
                                    <Typography fontWeight={500}>{report.referringDoctor || '-'}</Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={3}>
                                    <Typography variant="caption" color="text.secondary">Exams Completed</Typography>
                                    <Typography fontWeight={500}>{report.completedExams?.join(', ') || '-'}</Typography>
                                </Grid>
                            </Grid>

                            <Divider sx={{ my: 3 }} />

                            {report.clinicalInfo && (
                                <Box mb={3}>
                                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
                                        Clinical Information
                                    </Typography>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{report.clinicalInfo}</Typography>
                                </Box>
                            )}

                            {report.findings && (
                                <Box mb={3}>
                                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
                                        Findings
                                    </Typography>
                                    <Typography sx={{ whiteSpace: 'pre-wrap' }}>{report.findings}</Typography>
                                </Box>
                            )}

                            {report.radiographicImpression && (
                                <Box mb={3}>
                                    <Typography variant="subtitle1" fontWeight={600} color="text.secondary" gutterBottom>
                                        Impression
                                    </Typography>
                                    <Paper
                                        variant="outlined"
                                        sx={{
                                            p: 2,
                                            bgcolor: 'primary.50',
                                            borderColor: 'primary.light',
                                            borderRadius: 2
                                        }}
                                    >
                                        <Typography sx={{ whiteSpace: 'pre-wrap', fontWeight: 500 }}>
                                            {report.radiographicImpression}
                                        </Typography>
                                    </Paper>
                                </Box>
                            )}
                        </Paper>
                    </Grid>
                )}

                {!report && order.report && (
                    <Grid item xs={12}>
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            The report is not yet completed. Please check back later.
                        </Alert>
                    </Grid>
                )}
            </Grid>
        </Box>
    );
};

export default ExternalOrderDetail;
